import { useCallback, useMemo, useState } from 'react';

import type { TLunaStreamGroup, TLunaStream, TUiStreamGroup } from '../types';
import { addUnique } from '../utils/array';
import { filterUiGroups } from '../utils/filterUiGroups';
import { isExactMatch, startsWithIgnoreCase } from '../utils/search';

export const NO_SOURCE_VALUE = 'null';

interface TUseSourceSearchArgs {
  uiGroups: TUiStreamGroup[];
  streams: TLunaStream[];
  groups: TLunaStreamGroup[];
  selectedSources: string[];
  customSourceNames: string[];
  onChangeSelectedSource: (next: string[]) => void;
  onChangeCustomSourceNames: (next: string[]) => void;
}

interface TUseSourceSearchResult {
  searchValue: string;
  setSearchValue: (next: string) => void;
  visibleUiGroups: TUiStreamGroup[];
  visibleCustomSourceNames: string[];
  canAdd: boolean;
  onAdd: VoidFunction;
}

export function useSourceSearch({
  uiGroups,
  streams,
  groups,
  selectedSources,
  customSourceNames,
  onChangeSelectedSource,
  onChangeCustomSourceNames,
}: TUseSourceSearchArgs): TUseSourceSearchResult {
  const [searchValue, setSearchValue] = useState('');

  const normalizedSearch = searchValue.trim();
  const hasSearchValue = normalizedSearch.length > 0;

  const { visibleUiGroups, visibleCustomSourceNames, canAdd } = useMemo(() => {
    if (!hasSearchValue) {
      return {
        visibleUiGroups: uiGroups,
        visibleCustomSourceNames: customSourceNames,
        canAdd: false,
      };
    }

    const $visibleCustomSourceNames = customSourceNames.filter((name) => startsWithIgnoreCase(name, normalizedSearch));
    const $visibleUiGroups = filterUiGroups(uiGroups, normalizedSearch);

    const hasExactMatch = streams.some((s) => isExactMatch(s.name, normalizedSearch))
      || customSourceNames.some((v) => isExactMatch(v, normalizedSearch));

    const hasGroupMatch = groups.some((g) => isExactMatch(g.group_name, normalizedSearch));

    const $canAdd = !hasExactMatch && !hasGroupMatch && normalizedSearch !== NO_SOURCE_VALUE;

    return {
      visibleUiGroups: $visibleUiGroups,
      visibleCustomSourceNames: $visibleCustomSourceNames,
      canAdd: $canAdd,
    };
  }, [customSourceNames, groups, hasSearchValue, normalizedSearch, streams, uiGroups]);

  const onAdd = useCallback(() => {
    if (!canAdd) return;

    onChangeCustomSourceNames(addUnique(customSourceNames, normalizedSearch));
    onChangeSelectedSource(addUnique(selectedSources, normalizedSearch));
    setSearchValue('');
  }, [
    canAdd,
    customSourceNames,
    onChangeCustomSourceNames,
    onChangeSelectedSource,
    normalizedSearch,
    selectedSources,
  ]);

  return {
    searchValue,
    setSearchValue,
    visibleUiGroups,
    visibleCustomSourceNames,
    canAdd,
    onAdd,
  };
}
